import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * This overrides the PrimeNG Aura theme to use Artemis theme colors.
 * Instead of hardcoding colors, we reference CSS custom properties that are
 * automatically generated from SCSS variables in _default-variables.scss.
 * This ensures a single source of truth - when you update a color in the SCSS
 * theme file, PrimeNG components will automatically use the updated color.
 *
 * The CSS variables are generated by the _generate-css-vars.scss mixin.
 */
export const AuraArtemis = definePreset(Aura, {
    semantic: {
        primary: {
            // Reference the --primary CSS variable from your theme
            50: 'color-mix(in srgb, var(--primary) 10%, white)',
            100: 'color-mix(in srgb, var(--primary) 20%, white)',
            200: 'color-mix(in srgb, var(--primary) 40%, white)',
            300: 'color-mix(in srgb, var(--primary) 60%, white)',
            400: 'color-mix(in srgb, var(--primary) 80%, white)',
            500: 'var(--primary)', // Main primary color
            600: 'color-mix(in srgb, var(--primary) 80%, black)',
            700: 'color-mix(in srgb, var(--primary) 60%, black)',
            800: 'color-mix(in srgb, var(--primary) 40%, black)',
            900: 'color-mix(in srgb, var(--primary) 20%, black)',
            950: 'color-mix(in srgb, var(--primary) 10%, black)',
        },
    },
    // Use CSS to override primitive colors used by severity components
    css: `
        :root {
            /* Map PrimeNG's green primitive to Bootstrap's success color */
            --p-green-50: color-mix(in srgb, var(--success) 10%, white);
            --p-green-100: color-mix(in srgb, var(--success) 20%, white);
            --p-green-200: color-mix(in srgb, var(--success) 40%, white);
            --p-green-300: color-mix(in srgb, var(--success) 60%, white);
            --p-green-400: color-mix(in srgb, var(--success) 80%, white);
            --p-green-500: var(--success);
            --p-green-600: color-mix(in srgb, var(--success) 80%, black);
            --p-green-700: color-mix(in srgb, var(--success) 60%, black);
            --p-green-800: color-mix(in srgb, var(--success) 40%, black);
            --p-green-900: color-mix(in srgb, var(--success) 20%, black);
            --p-green-950: color-mix(in srgb, var(--success) 10%, black);

            /* Map PrimeNG's red primitive to Bootstrap's danger color */
            --p-red-50: color-mix(in srgb, var(--danger) 10%, white);
            --p-red-100: color-mix(in srgb, var(--danger) 20%, white);
            --p-red-200: color-mix(in srgb, var(--danger) 40%, white);
            --p-red-300: color-mix(in srgb, var(--danger) 60%, white);
            --p-red-400: color-mix(in srgb, var(--danger) 80%, white);
            --p-red-500: var(--danger);
            --p-red-600: color-mix(in srgb, var(--danger) 80%, black);
            --p-red-700: color-mix(in srgb, var(--danger) 60%, black);
            --p-red-800: color-mix(in srgb, var(--danger) 40%, black);
            --p-red-900: color-mix(in srgb, var(--danger) 20%, black);
            --p-red-950: color-mix(in srgb, var(--danger) 10%, black);

            /* Map PrimeNG's yellow primitive to Bootstrap's warning color */
            --p-yellow-50: color-mix(in srgb, var(--warning) 10%, white);
            --p-yellow-100: color-mix(in srgb, var(--warning) 20%, white);
            --p-yellow-200: color-mix(in srgb, var(--warning) 40%, white);
            --p-yellow-300: color-mix(in srgb, var(--warning) 60%, white);
            --p-yellow-400: color-mix(in srgb, var(--warning) 80%, white);
            --p-yellow-500: var(--warning);
            --p-yellow-600: color-mix(in srgb, var(--warning) 80%, black);
            --p-yellow-700: color-mix(in srgb, var(--warning) 60%, black);
            --p-yellow-800: color-mix(in srgb, var(--warning) 40%, black);
            --p-yellow-900: color-mix(in srgb, var(--warning) 20%, black);
            --p-yellow-950: color-mix(in srgb, var(--warning) 10%, black);

            /* Map PrimeNG's orange primitive to Bootstrap's orange color */
            --p-orange-50: color-mix(in srgb, var(--orange) 10%, white);
            --p-orange-100: color-mix(in srgb, var(--orange) 20%, white);
            --p-orange-200: color-mix(in srgb, var(--orange) 40%, white);
            --p-orange-300: color-mix(in srgb, var(--orange) 60%, white);
            --p-orange-400: color-mix(in srgb, var(--orange) 80%, white);
            --p-orange-500: var(--orange);
            --p-orange-600: color-mix(in srgb, var(--orange) 80%, black);
            --p-orange-700: color-mix(in srgb, var(--orange) 60%, black);
            --p-orange-800: color-mix(in srgb, var(--orange) 40%, black);
            --p-orange-900: color-mix(in srgb, var(--orange) 20%, black);
            --p-orange-950: color-mix(in srgb, var(--orange) 10%, black);

            /* Map PrimeNG's cyan primitive to Bootstrap's info color */
            --p-cyan-50: color-mix(in srgb, var(--info) 10%, white);
            --p-cyan-100: color-mix(in srgb, var(--info) 20%, white);
            --p-cyan-200: color-mix(in srgb, var(--info) 40%, white);
            --p-cyan-300: color-mix(in srgb, var(--info) 60%, white);
            --p-cyan-400: color-mix(in srgb, var(--info) 80%, white);
            --p-cyan-500: var(--info);
            --p-cyan-600: color-mix(in srgb, var(--info) 80%, black);
            --p-cyan-700: color-mix(in srgb, var(--info) 60%, black);
            --p-cyan-800: color-mix(in srgb, var(--info) 40%, black);
            --p-cyan-900: color-mix(in srgb, var(--info) 20%, black);
            --p-cyan-950: color-mix(in srgb, var(--info) 10%, black);
        }
    `,
});
